import { startCase } from 'es-toolkit';

import { upperFirstEveryWord } from '@zg-rentals/web-base';

import { PropertyType } from '@zg-rentals/ts-rental-manager-api-client';

import { PROPERTY_TYPES, PROPERTY_TYPES_BY_ENUM, SINGLE_CONDO_LABEL } from '../constants';

import type { AccountViewListingGroupType } from '@zg-rentals/ts-rental-manager-api-client';

// Special chars (excluding underscore) stripped from the PMR results URL address
// segment. Must match rental-growth-web's encodeFullAddressForUrl so the address
// decodes back correctly on the price-my-rental results page.
const PMR_ADDRESS_SPECIAL_CHARS = /[!"#$%&'()*+,-./:;<=>?@[\]^`{|}~]/g;

export const formatAddress = (
  addressComponents: Partial<{
    street: string;
    city: string;
    state: string;
    unit: string;
    name: string;
    zip: string;
  }>,
) => {
  const { street, city, state, unit, name, zip } = addressComponents;

  // Transformations
  let formattedStreet = street ? upperFirstEveryWord(street) : '';
  const formattedCity = city ? startCase(city) : '';
  const formattedState = state ? state.toUpperCase() : '';
  const formattedUnit = unit ? `#${unit.toUpperCase()}` : '';
  const formattedName = name ? upperFirstEveryWord(name) : '';

  const formattedAddressWithoutUnit = [formattedStreet, formattedCity, `${formattedState} ${zip}`]
    .filter((val) => val)
    .join(', ');

  if (unit && unit.length) {
    formattedStreet += ` ${formattedUnit}`;
  }

  const formattedAddress = [formattedStreet, formattedCity, formattedState].filter((val) => val).join(', ');
  const formattedCityState = [formattedCity, formattedState].filter((val) => val).join(', ');
  const formattedCityStateZip = `${formattedCityState} ${zip}`;
  const formattedCompleteAddress = [`${formattedStreet},`, formattedCityState, zip].filter((val) => val).join(' ');

  // NOTE: formattedKebobCase is mainly used to build /price-my-rental/results/ URLs.
  // Encode the address path segment the same way rental-growth-web's
  // encodeFullAddressForUrl does: commas become underscores (the delimiter the
  // results page decodes back to commas), other special chars are stripped, and
  // spaces become hyphens. Callers append the trailing slash and ?unit= param.
  const formattedKebobCase = encodeURIComponent(
    formattedAddressWithoutUnit
      .replaceAll(',', '_')
      .replace(PMR_ADDRESS_SPECIAL_CHARS, '')
      .replaceAll(' ', '-')
      .toLowerCase(),
  );

  return {
    formattedAddressWithoutUnit,
    formattedAddress,
    formattedCityState,
    formattedCityStateZip,
    formattedCompleteAddress,
    formattedName,
    formattedStreet,
    formattedKebobCase,
  };
};

export const getPropertyTypeLabelByEnum = (propertyType?: string) => {
  const k = propertyType?.trim();
  return k && Object.hasOwn(PROPERTY_TYPES_BY_ENUM, k) ? PROPERTY_TYPES_BY_ENUM[k] : undefined;
};

export const getPropertyTypeLabel = ({
  type,
  propertyType,
}: {
  type?: AccountViewListingGroupType;
  propertyType?: string;
}) => {
  if (type === 'MULTIPLEX') {
    return PROPERTY_TYPES.CONDO;
  }
  if (type === 'LISTING') {
    return propertyType?.trim() === PropertyType.CONDO ? SINGLE_CONDO_LABEL : getPropertyTypeLabelByEnum(propertyType);
  }
  return undefined;
};
